import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Pause, Play } from "@phosphor-icons/react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { normalizeLang } from "@/lib/i18n";

export const COPY = {
  en: { play: "Play your recording", pause: "Pause your recording", seek: "Recording position" },
  "pt-BR": { play: "Reproduzir sua gravação", pause: "Pausar sua gravação", seek: "Posição da gravação" },
  "es-419": { play: "Reproducir su grabación", pause: "Pausar su grabación", seek: "Posición de la grabación" },
  "fr-CA": { play: "Lire votre enregistrement", pause: "Mettre votre enregistrement en pause", seek: "Position dans l’enregistrement" },
};

function formatClock(value) {
  const safe = Number.isFinite(value) && value >= 0 ? value : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = Math.floor(safe % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function PlaybackControls({ audio, copy }) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!audio) return undefined;

    const syncTime = () => setCurrentTime(Number.isFinite(audio.currentTime) ? audio.currentTime : 0);
    const syncDuration = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      syncTime();
    };

    syncTime();
    syncDuration();
    audio.addEventListener("timeupdate", syncTime);
    audio.addEventListener("loadedmetadata", syncDuration);
    audio.addEventListener("durationchange", syncDuration);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", syncTime);
      audio.removeEventListener("loadedmetadata", syncDuration);
      audio.removeEventListener("durationchange", syncDuration);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [audio]);

  const togglePlayback = async () => {
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    if (duration > 0 && audio.currentTime >= duration - 0.05) {
      audio.currentTime = 0;
    }
    try {
      await audio.play();
    } catch {
      // A failed local preview should not interfere with the interview flow.
    }
  };

  const seek = (event) => {
    if (!audio) return;
    const next = Number(event.target.value);
    audio.currentTime = Number.isFinite(next) ? next : 0;
    setCurrentTime(audio.currentTime);
  };

  return (
    <div
      className="flex w-full min-w-0 items-center gap-3 rounded-2xl border border-[color:var(--border-stone)] bg-[color:var(--bg-base)] px-3 py-2.5"
      data-testid="custom-recording-playback"
    >
      <button
        type="button"
        onClick={togglePlayback}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--brand)] text-white hover:bg-[color:var(--brand-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
        aria-label={playing ? copy.pause : copy.play}
        data-testid="custom-recording-play-pause"
      >
        {playing ? <Pause weight="fill" size={18} /> : <Play weight="fill" size={18} />}
      </button>

      <input
        type="range"
        min="0"
        max={Math.max(duration, 0)}
        step="0.05"
        value={Math.min(currentTime, Math.max(duration, 0))}
        onChange={seek}
        className="min-w-0 flex-1 accent-[color:var(--brand)]"
        aria-label={copy.seek}
        data-testid="custom-recording-seek"
      />

      <div className="shrink-0 whitespace-nowrap font-mono text-xs text-[color:var(--text-secondary)]">
        {formatClock(currentTime)} / {formatClock(duration)}
      </div>
    </div>
  );
}

/**
 * Replaces native browser recording controls in both the warm-up and the real
 * interview. Native mobile audio controls expose an unwanted Download menu and
 * vary by browser. The custom player keeps only Play/Pause, seek and time.
 */
export default function WarmupPlaybackEnhancer() {
  const location = useLocation();
  const { user } = useAuth();
  const [target, setTarget] = useState(null);
  const [audio, setAudio] = useState(null);
  const [kind, setKind] = useState(null);
  const lang = normalizeLang(user?.preferred_language);
  const copy = useMemo(() => COPY[lang] || COPY.en, [lang]);
  const isInterview = /^\/interview\/[^/]+$/.test(location.pathname);

  useEffect(() => {
    if (!isInterview || typeof document === "undefined") {
      setTarget(null);
      setAudio(null);
      setKind(null);
      return undefined;
    }

    let observer = null;
    let activeAudio = null;
    let host = null;
    let parent = null;
    let priorControls = true;
    let priorAriaHidden = null;
    let activeKind = null;

    const detach = () => {
      if (activeAudio) {
        activeAudio.controls = priorControls;
        if (priorAriaHidden === null) activeAudio.removeAttribute("aria-hidden");
        else activeAudio.setAttribute("aria-hidden", priorAriaHidden);
        activeAudio.classList.remove("recording-native-playback-hidden");
      }
      if (parent) {
        parent.classList.remove("recording-playback-layout");
        parent.classList.remove("recording-playback-layout-warmup");
        parent.classList.remove("recording-playback-layout-interview");
      }
      if (host?.parentElement) host.remove();
      activeAudio = null;
      host = null;
      parent = null;
      activeKind = null;
      setTarget(null);
      setAudio(null);
      setKind(null);
    };

    const sync = () => {
      const warmupAudio = document.querySelector('[data-testid="warmup-playback"]');
      const interviewAudio = document.querySelector('[data-testid="playback-button"]');
      const nextAudio = warmupAudio || interviewAudio;
      const nextKind = warmupAudio ? "warmup" : interviewAudio ? "interview" : null;

      if (!nextAudio) {
        if (activeAudio) detach();
        return false;
      }
      if (nextAudio === activeAudio && nextKind === activeKind && host?.parentElement) return true;

      detach();
      activeAudio = nextAudio;
      activeKind = nextKind;
      parent = nextAudio.parentElement;
      if (!parent) return false;

      priorControls = nextAudio.controls;
      priorAriaHidden = nextAudio.getAttribute("aria-hidden");
      nextAudio.controls = false;
      nextAudio.removeAttribute("controls");
      nextAudio.setAttribute("aria-hidden", "true");
      nextAudio.classList.add("recording-native-playback-hidden");
      parent.classList.add("recording-playback-layout", `recording-playback-layout-${nextKind}`);

      host = document.createElement("div");
      host.setAttribute("data-testid", "custom-recording-playback-host");
      host.className = nextKind === "warmup"
        ? "min-w-0 flex-1"
        : "mt-3 min-w-0 w-full";
      parent.insertBefore(host, nextAudio);
      setAudio(nextAudio);
      setTarget(host);
      setKind(nextKind);
      return true;
    };

    sync();
    observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (observer) observer.disconnect();
      detach();
    };
  }, [isInterview, location.pathname]);

  if (!isInterview) return null;

  return (
    <>
      <style>{`
        .recording-native-playback-hidden {
          display: none !important;
        }

        .recording-playback-layout {
          min-width: 0;
        }

        @media (max-width: 639px) {
          .recording-playback-layout-warmup {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          .recording-playback-layout-warmup > [data-testid="custom-recording-playback-host"] {
            width: 100% !important;
            flex: 1 1 auto !important;
          }

          .recording-playback-layout-warmup > [data-testid="warmup-rerecord"] {
            width: 100% !important;
            justify-content: center !important;
          }

          [data-testid="custom-recording-playback"] {
            gap: 0.625rem !important;
            padding-left: 0.625rem !important;
            padding-right: 0.625rem !important;
          }

          [data-testid="custom-recording-playback"] > div:last-child {
            font-size: 0.6875rem !important;
          }

          /* The tablet treatment intentionally allows horizontal scrolling,
             but phones must never require it. Long translated labels reflow
             into a stable two-column action grid. */
          [data-testid="interview-container"] main section > div.mb-4 > div:last-child {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: visible !important;
            gap: 0.5rem !important;
            padding-bottom: 0 !important;
            align-items: stretch !important;
          }

          [data-testid="interview-container"] main section > div.mb-4 > div:last-child > button,
          [data-testid="interview-container"] main section > div.mb-4 > div:last-child > div {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }

          [data-testid="interview-container"] main section > div.mb-4 > div:last-child button {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            min-height: 3rem !important;
            height: auto !important;
            padding: 0.625rem 0.75rem !important;
            justify-content: center !important;
            white-space: normal !important;
            text-align: center !important;
            line-height: 1.15 !important;
          }

          /* On phones the recorder card must not render a second set of
             Re-record / Submit controls underneath the dedicated sticky CTA. */
          [data-testid="interview-container"] main [data-testid="re-record-button"],
          [data-testid="interview-container"] main [data-testid="submit-answer-button"] {
            display: none !important;
          }

          /* The sticky phone CTA is the single source of answer actions. */
          [data-testid="interview-mobile-cta"] {
            width: 100% !important;
            max-width: 100vw !important;
            box-sizing: border-box !important;
            overflow-x: hidden !important;
          }

          [data-testid="interview-mobile-cta"] > div {
            display: grid !important;
            grid-template-columns: 3rem minmax(0, 1fr) !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            gap: 0.5rem !important;
            align-items: center !important;
          }

          [data-testid="interview-mobile-rerecord"] {
            width: 3rem !important;
            min-width: 3rem !important;
            max-width: 3rem !important;
            padding: 0 !important;
          }

          [data-testid="interview-mobile-submit"] {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: 3rem !important;
            padding: 0.625rem 0.75rem !important;
            white-space: normal !important;
            text-align: center !important;
            line-height: 1.1 !important;
            overflow: hidden !important;
          }

          [data-testid="interview-mobile-submit"] svg {
            flex: 0 0 auto !important;
          }
        }

        @media (max-width: 390px) {
          /* At the smallest phone width the recording player can sit in a
             narrow column beside the recorded-state checkmark. Put the time
             beside Play and give the scrubber a full second row so neither
             control is crushed. */
          [data-testid="custom-recording-playback"] {
            display: grid !important;
            grid-template-columns: 2.5rem minmax(0, 1fr) !important;
            grid-template-rows: auto auto !important;
            column-gap: 0.625rem !important;
            row-gap: 0.5rem !important;
            align-items: center !important;
          }

          [data-testid="custom-recording-play-pause"] {
            grid-column: 1 !important;
            grid-row: 1 !important;
          }

          [data-testid="custom-recording-seek"] {
            grid-column: 1 / -1 !important;
            grid-row: 2 !important;
            width: 100% !important;
            min-width: 0 !important;
          }

          [data-testid="custom-recording-playback"] > div:last-child {
            grid-column: 2 !important;
            grid-row: 1 !important;
            justify-self: end !important;
            font-size: 0.6875rem !important;
          }

          [data-testid="interview-container"] main section > div.mb-4 > div:last-child button,
          [data-testid="interview-mobile-submit"] {
            font-size: 0.8125rem !important;
          }
        }
      `}</style>
      {target && audio ? createPortal(<PlaybackControls audio={audio} copy={copy} key={kind || "recording"} />, target) : null}
    </>
  );
}
